import type { ExportSlide } from "@/services/api.service";

const TEXT_KEYS = new Set([
  "answer",
  "body",
  "description",
  "explanation",
  "heading",
  "label",
  "name",
  "prompt",
  "question",
  "subtitle",
  "text",
  "title",
  "value",
]);

const ALT_TEXT_KEYS = new Set([
  "alt",
  "aria_label",
  "caption",
  "label",
  "name",
  "title",
]);
const IMAGE_URL_KEYS = new Set([
  "image",
  "image_url",
  "imageurl",
  "src",
  "thumbnail",
  "thumbnail_url",
  "thumbnailurl",
  "url",
]);

const TECHNICAL_KEYS = new Set([
  "created_at",
  "email",
  "id",
  "metadata",
  "slide_id",
  "updated_at",
  "url",
  "user_id",
  "uuid",
]);

const MAX_BLOCKS = 8;
const MAX_IMAGES = 6;
const MAX_TEXT_LENGTH = 260;

export type SlidePreviewImage = {
  url: string;
  alt: string;
};

const cleanText = (value: string): string => value.replace(/\s+/g, " ").trim();

const normalizeKey = (key: string): string =>
  key.toLowerCase().replace(/[-\s]/g, "_");

const isReadableText = (value: string): boolean => {
  const text = cleanText(value);
  if (text.length < 2) return false;
  if (/^https?:\/\//i.test(text)) return false;
  if (/^[a-f0-9-]{12,}$/i.test(text)) return false;
  return /[a-zA-Z]/.test(text);
};

const pushUnique = (blocks: string[], value: string) => {
  const text = cleanText(value);
  if (!isReadableText(text)) return;
  const clipped =
    text.length > MAX_TEXT_LENGTH
      ? `${text.slice(0, MAX_TEXT_LENGTH - 1).trim()}...`
      : text;
  if (!blocks.includes(clipped) && blocks.length < MAX_BLOCKS) {
    blocks.push(clipped);
  }
};

const isProbablyImageUrl = (value: string): boolean => {
  const url = value.trim();
  if (/^data:image\//i.test(url)) return true;
  if (!/^https?:\/\//i.test(url)) return false;
  return /\.(png|jpe?g|gif|webp|svg|avif)(?:[?#].*)?$/i.test(url);
};

const getAltText = (record: Record<string, unknown>): string | undefined => {
  for (const [key, value] of Object.entries(record)) {
    if (typeof value === "string" && ALT_TEXT_KEYS.has(normalizeKey(key))) {
      const text = cleanText(value);
      if (text) return text;
    }
  }
  return undefined;
};

const pushImage = (
  images: SlidePreviewImage[],
  value: string,
  altText?: string,
) => {
  const url = value.trim();
  if (!url || images.some((image) => image.url === url) || images.length >= MAX_IMAGES) {
    return;
  }
  images.push({
    url,
    alt: altText || `Slide image ${images.length + 1}`,
  });
};

const collectTextBlocks = (
  value: unknown,
  blocks: string[],
  parentKey = "",
  depth = 0,
) => {
  if (blocks.length >= MAX_BLOCKS || depth > 7 || value == null) return;

  if (typeof value === "string") {
    if (TEXT_KEYS.has(parentKey)) {
      pushUnique(blocks, value);
    }
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item) => collectTextBlocks(item, blocks, parentKey, depth + 1));
    return;
  }

  if (typeof value !== "object") return;

  Object.entries(value as Record<string, unknown>).forEach(([key, child]) => {
    const normalizedKey = key.toLowerCase();
    if (TECHNICAL_KEYS.has(normalizedKey)) return;
    collectTextBlocks(child, blocks, normalizedKey, depth + 1);
  });
};

const collectImageUrls = (
  value: unknown,
  images: SlidePreviewImage[],
  parentKey = "",
  inheritedAlt?: string,
  inImageContext = false,
  depth = 0,
) => {
  if (images.length >= MAX_IMAGES || depth > 7 || value == null) return;

  if (typeof value === "string") {
    const normalizedParentKey = normalizeKey(parentKey);
    if (
      IMAGE_URL_KEYS.has(normalizedParentKey) &&
      (inImageContext ||
        normalizedParentKey.includes("image") ||
        normalizedParentKey.includes("thumbnail") ||
        isProbablyImageUrl(value))
    ) {
      pushImage(images, value, inheritedAlt);
    }
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item) =>
      collectImageUrls(item, images, parentKey, inheritedAlt, inImageContext, depth + 1),
    );
    return;
  }

  if (typeof value !== "object") return;

  const record = value as Record<string, unknown>;
  const ownAlt = getAltText(record) || inheritedAlt;
  const typeValue = typeof record.type === "string" ? normalizeKey(record.type) : "";
  const currentImageContext =
    inImageContext ||
    typeValue === "image" ||
    normalizeKey(parentKey).includes("image") ||
    normalizeKey(parentKey).includes("thumbnail");

  Object.entries(record).forEach(([key, child]) => {
    collectImageUrls(child, images, key, ownAlt, currentImageContext, depth + 1);
  });
};

export const getSlidePreviewBlocks = (
  slide: Pick<ExportSlide, "content" | "flow" | "metadata">,
): string[] => {
  const blocks: string[] = [];
  collectTextBlocks(slide.content, blocks);
  collectTextBlocks(slide.flow, blocks);
  collectTextBlocks(slide.metadata, blocks);
  return blocks;
};

export const getSlidePreviewImages = (
  slide: Pick<ExportSlide, "content" | "flow" | "metadata">,
): SlidePreviewImage[] => {
  const images: SlidePreviewImage[] = [];
  collectImageUrls(slide.content, images);
  collectImageUrls(slide.flow, images);
  collectImageUrls(slide.metadata, images);
  return images;
};

export const formatSlideStatusLabel = (
  status: string,
  lessonIsPublished: boolean,
): string => {
  const normalized = status.trim().toUpperCase();
  if (lessonIsPublished && normalized === "DRAFT") {
    return "Published";
  }
  return normalized
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};
